import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { waLink, getLenis } from "./data";
import { EASE } from "./ui-bits";

const NEEDS = ["Retail", "Restaurant / F&B", "Office", "QSR", "Other"];
const AREAS = ["Indiranagar", "Koramangala", "HSR", "Whitefield", "CBD", "Other"];
const PROP_TYPES = ["Retail", "Office", "Restaurant / F&B", "Commercial Building", "Other"];

const Pill = ({ active, children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`border px-5 py-3 text-[12px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
      active
        ? "border-ivory bg-ivory text-obsidian"
        : "border-ivory/25 text-ivory/80 hover:border-ivory/60"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, value, onChange, placeholder, testId }) => (
  <label className="block">
    <span className="text-[10px] font-medium uppercase tracking-[0.26em] text-stone">
      {label}
    </span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-2 w-full border-b border-ivory/20 bg-transparent py-3 text-base text-ivory placeholder:text-stone/40 focus:border-champagne"
    />
  </label>
);

const Shell = ({ title, stepLabel, onClose, children, testId }) => (
  <motion.div
    className="fixed inset-0 z-[160] overflow-y-auto overscroll-contain bg-obsidian"
    data-lenis-prevent
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: EASE }}
    data-testid={testId}
  >
    <div className="mx-auto flex min-h-full max-w-2xl flex-col px-6 py-8 md:py-12">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-champagne">
            Elevate Estates
          </div>
          <h2 className="mt-2 font-serif text-3xl uppercase text-ivory md:text-4xl">
            {title}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-11 w-11 items-center justify-center border border-ivory/20 text-ivory transition-colors duration-300 hover:border-champagne hover:text-champagne"
          data-testid={`${testId}-close`}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
      {stepLabel && (
        <div className="mt-8 text-[11px] font-medium uppercase tracking-[0.3em] text-stone">
          {stepLabel}
        </div>
      )}
      <div className="mt-6 flex-1">{children}</div>
    </div>
  </motion.div>
);

const FindSpace = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const [need, setNeed] = useState("");
  const [locs, setLocs] = useState([]);
  const [area, setArea] = useState("");
  const [budget, setBudget] = useState("");
  const [pref, setPref] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");

  const toggleLoc = (l) =>
    setLocs((p) => (p.includes(l) ? p.filter((x) => x !== l) : [...p, l]));

  const canNext =
    step === 0 ? !!need : step === 1 ? locs.length > 0 : true;

  const submit = () => {
    const msg = [
      "Hi Elevate Estates,",
      "I'm looking for a commercial space.",
      `Requirement: ${need}`,
      `Location: ${locs.join(", ")}`,
      area && `Area: ${area} sq ft`,
      budget && `Budget: ₹${budget} / month`,
      pref && `Preferred location: ${pref}`,
      name && `Name: ${name}`,
      company && `Company / Brand: ${company}`,
      phone && `Phone: ${phone}`,
      "I'd like to discuss available opportunities.",
    ]
      .filter(Boolean)
      .join("\n");
    window.open(waLink(msg), "_blank");
  };

  return (
    <Shell
      title="Find Your Space"
      stepLabel={`Step 0${step + 1} / 04`}
      onClose={onClose}
      testId="find-space-modal"
    >
      <div className="mb-8 h-px w-full bg-ivory/10">
        <motion.div
          className="h-px bg-champagne"
          animate={{ width: `${((step + 1) / 4) * 100}%` }}
          transition={{ duration: 0.5, ease: EASE }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          {step === 0 && (
            <div>
              <p className="mb-6 font-serif text-2xl text-ivory">What are you looking for?</p>
              <div className="flex flex-wrap gap-3">
                {NEEDS.map((n) => (
                  <Pill key={n} active={need === n} onClick={() => setNeed(n)} testId={`find-need-${n.toLowerCase().replace(/[\s/&]+/g, "-")}`}>
                    {n}
                  </Pill>
                ))}
              </div>
            </div>
          )}
          {step === 1 && (
            <div>
              <p className="mb-6 font-serif text-2xl text-ivory">Where?</p>
              <div className="flex flex-wrap gap-3">
                {AREAS.map((a) => (
                  <Pill key={a} active={locs.includes(a)} onClick={() => toggleLoc(a)} testId={`find-loc-${a.toLowerCase()}`}>
                    {a}
                  </Pill>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-8">
              <p className="font-serif text-2xl text-ivory">Tell us about the requirement</p>
              <Field label="Space required (sq ft)" value={area} onChange={setArea} placeholder="e.g. 2,000 – 2,500" testId="find-area-input" />
              <Field label="Budget (₹ / month)" value={budget} onChange={setBudget} placeholder="e.g. 2,00,000" testId="find-budget-input" />
              <Field label="Preferred location" value={pref} onChange={setPref} placeholder="e.g. 100 Feet Road, Indiranagar" testId="find-pref-input" />
            </div>
          )}
          {step === 3 && (
            <div className="space-y-8">
              <p className="font-serif text-2xl text-ivory">About you</p>
              <Field label="Name" value={name} onChange={setName} placeholder="Your name" testId="find-name-input" />
              <Field label="Company / Brand" value={company} onChange={setCompany} placeholder="Your company or brand" testId="find-company-input" />
              <Field label="Phone" value={phone} onChange={setPhone} placeholder="Your phone number" testId="find-phone-input" />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-12 flex items-center justify-between pb-6">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          className={`inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.24em] text-stone transition-opacity ${step === 0 ? "pointer-events-none opacity-0" : "hover:text-ivory"}`}
          data-testid="find-back-btn"
        >
          <ArrowLeft size={13} /> Back
        </button>
        {step < 3 ? (
          <button
            type="button"
            onClick={() => canNext && setStep((s) => s + 1)}
            className={`group inline-flex items-center gap-3 px-7 py-4 text-[12px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
              canNext ? "bg-ivory text-obsidian hover:bg-champagne" : "cursor-not-allowed bg-ivory/20 text-ivory/40"
            }`}
            data-testid="find-next-btn"
          >
            Continue <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            className="group inline-flex items-center gap-3 bg-champagne px-7 py-4 text-[12px] font-medium uppercase tracking-[0.22em] text-obsidian transition-opacity duration-300 hover:opacity-90"
            data-testid="find-submit-whatsapp"
          >
            Send Requirement via WhatsApp <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
          </button>
        )}
      </div>
    </Shell>
  );
};

const ListProperty = ({ onClose }) => {
  const [type, setType] = useState("");
  const [loc, setLoc] = useState("");
  const [size, setSize] = useState("");
  const [rent, setRent] = useState("");
  const [addr, setAddr] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  const submit = () => {
    const msg = [
      "Hi Elevate Estates,",
      "I'd like to list my property.",
      type && `Property Type: ${type}`,
      loc && `Location: ${loc}`,
      size && `Property Size: ${size}`,
      rent && `Expected Rent: ${rent}`,
      addr && `Property Address: ${addr}`,
      name && `Name: ${name}`,
      phone && `Phone: ${phone}`,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(waLink(msg), "_blank");
  };

  return (
    <Shell title="List Your Property" onClose={onClose} testId="list-property-modal">
      <div className="space-y-8">
        <div>
          <span className="text-[10px] font-medium uppercase tracking-[0.26em] text-stone">
            Property Type
          </span>
          <div className="mt-4 flex flex-wrap gap-3">
            {PROP_TYPES.map((t) => (
              <Pill key={t} active={type === t} onClick={() => setType(t)} testId={`list-type-${t.toLowerCase().replace(/[\s/&]+/g, "-")}`}>
                {t}
              </Pill>
            ))}
          </div>
        </div>
        <Field label="Location" value={loc} onChange={setLoc} placeholder="e.g. Koramangala" testId="list-location-input" />
        <Field label="Property Size" value={size} onChange={setSize} placeholder="e.g. 3,200 sq ft" testId="list-size-input" />
        <Field label="Expected Rent" value={rent} onChange={setRent} placeholder="e.g. ₹3.5L / month" testId="list-rent-input" />
        <Field label="Property Address" value={addr} onChange={setAddr} placeholder="Full address" testId="list-address-input" />
        <div className="grid gap-8 sm:grid-cols-2">
          <Field label="Your Name" value={name} onChange={setName} placeholder="Your name" testId="list-name-input" />
          <Field label="Phone" value={phone} onChange={setPhone} placeholder="Your phone number" testId="list-phone-input" />
        </div>
      </div>
      <div className="mt-12 pb-6">
        <button
          type="button"
          onClick={submit}
          className="group inline-flex w-full items-center justify-center gap-3 bg-champagne px-7 py-4 text-[12px] font-medium uppercase tracking-[0.22em] text-obsidian transition-opacity duration-300 hover:opacity-90 sm:w-auto"
          data-testid="list-submit-whatsapp"
        >
          Connect with Elevate <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
        </button>
      </div>
    </Shell>
  );
};

const EnquiryModals = () => {
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const handler = (e) => setModal(e.detail);
    window.addEventListener("elevate:modal", handler);
    return () => window.removeEventListener("elevate:modal", handler);
  }, []);

  useEffect(() => {
    const lenis = getLenis();
    if (modal) {
      lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      lenis?.start();
      document.body.style.overflow = "";
    };
  }, [modal]);

  const close = () => setModal(null);

  return (
    <AnimatePresence>
      {modal === "find" && <FindSpace key="find" onClose={close} />}
      {modal === "list" && <ListProperty key="list" onClose={close} />}
    </AnimatePresence>
  );
};

export default EnquiryModals;
